import { useCallback, useState } from 'react';
import { notificationService } from '@/lib/services/notification.service';
import type { Annotation } from '../../model/annotation';
import type { CommentData, CommentThreadActions } from './CommentThread.types';
import { useTranslation } from 'react-i18next';

/**
 * Annotation-agnostic action handlers shared by the annotated (asset) and
 * plain (brief) threads. Owns the delete-dialog state and the
 * delete/reply/edit/copy-link/resolve handlers, all driven through the injected
 * `actions` so neither target's hooks are hard-wired here.
 *
 * Calls ONLY annotation-agnostic hooks (useState, useCallback). The save
 * handlers' signatures stay annotation-AWARE so the annotated thread can
 * forward draw payloads; the plain thread simply never passes an annotation
 * (and the brief actions ignore it).
 */
export function useThreadActions(comment: CommentData, actions: CommentThreadActions) {
  const { t } = useTranslation('projects');

  const { createComment, updateComment, deleteComment, isDeleting, resolveComment } = actions;
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const handleConfirmDelete = useCallback(async () => {
    try {
      await deleteComment(comment.id, comment.parentId, comment.replyCount);
      setShowDeleteDialog(false);
    } catch {
      // Error notification is shown by the mutation's onError handler
    }
  }, [deleteComment, comment.id, comment.parentId, comment.replyCount]);

  const handleReplySave = useCallback(
    async (
      content: Record<string, unknown>,
      annotation: Annotation | null | undefined,
      attachmentAssetIds: string[],
    ) => {
      await createComment(content, {
        parentId: comment.id,
        ...(annotation ? { annotation } : {}),
        attachmentAssetIds,
      });
    },
    [createComment, comment.id],
  );

  const handleEditSave = useCallback(
    async (
      content: Record<string, unknown>,
      annotation: Annotation | null | undefined,
      attachmentAssetIds: string[],
    ) => {
      await updateComment(comment.id, content, {
        ...(annotation === undefined ? {} : { annotation }),
        attachmentAssetIds,
      });
    },
    [updateComment, comment.id],
  );

  const handleCopyLink = useCallback(async () => {
    const url = new URL(window.location.href);
    url.searchParams.set('cid', comment.id);
    try {
      await navigator.clipboard.writeText(url.toString());
      notificationService.showSuccess(t('commentOnFile.useThreadActions.linkCopiedToClipboard'), {
        preventDuplicate: true,
      });
    } catch {
      notificationService.showError(t('commentOnFile.useThreadActions.couldNotCopyLink'));
    }
  }, [comment.id, t]);

  const isResolved = comment.status === 'resolved';
  const handleResolve = useCallback(() => {
    void resolveComment(comment.id, isResolved ? 'pending' : 'resolved');
  }, [resolveComment, comment.id, isResolved]);

  return {
    handleConfirmDelete,
    handleReplySave,
    handleEditSave,
    handleCopyLink,
    handleResolve,
    showDeleteDialog,
    setShowDeleteDialog,
    isDeleting,
  };
}
